import React, { useState, useEffect, useRef, useContext } from 'react';
import API_URL from '../apiConfig';
import { Link, useNavigate, useLocation } from 'react-router-dom'; // Add useLocation import
import './Sidebar.css'; // Import the CSS file
// Placeholder imports for SVG icons
import { ReactComponent as HomeIcon } from './icons/home.svg';
import { ReactComponent as UpcomingIcon } from './icons/consultation.svg';
import { ReactComponent as PastIcon } from './icons/past_consultation.svg';
import { ReactComponent as GradesIcon } from './icons/grade.svg';
import { ReactComponent as BellIcon } from './icons/bell.svg';
import { ReactComponent as SettingsIcon } from './icons/setting.svg';
import { ReactComponent as ClassRecorderIcon } from './icons/classRecord.svg';
import { ReactComponent as PointerIcon } from './icons/pointer.svg'; // NEW import
import { ReactComponent as UserAdd } from './icons/user-add.svg';
import { ReactComponent as CourseAdd } from './icons/CourseAdd.svg'; 
import { ReactComponent as ProgramAdd } from './icons/Code.svg'; // NEW: import profile icon
import { ReactComponent as DepartmentAdd } from './icons/Briefcase.svg';
import { ReactComponent as SemesterAdd } from './icons/Timer.svg';
import { ReactComponent as ScheduleIcon } from './icons/schedule.svg';
import { ReactComponent as ComparativeIcon } from './icons/Comparative.svg';
import { ReactComponent as VenuesIcon } from './icons/flag-2.svg';
import { ReactComponent as PeriodsIcon } from './icons/grid-2.svg';
import logo from './icons/logo2.png';
// Import missing icons from react-icons/fa
import { ReactComponent as Leaderboard } from './icons/ranking.svg';
// NEW: helper for profile picture
import { getProfilePictureUrl } from '../utils/utils';
import ProfilePictureUploader from './ProfilePictureUploader';
import SettingsPopup from './SettingsPopup';
import NotificationTray from './NotificationTray'; // Import NotificationTray component
import { useToast } from '../contexts/ToastContext'; // Import useToast for notification state

const Sidebar = ({ onExpandChange }) => {
  // Get unread notification count from ToastContext
  const { notifications } = useToast();
  const unreadCount = notifications ? notifications.filter(n => !n.read).length : 0;
  const location = useLocation(); // Add this hook
  const [isOpen, setIsOpen] = useState(false);
  const [userRole, setUserRole] = useState('');
  // NEW: state for profile picture URL
  const [profilePicture, setProfilePicture] = useState('');
  const [userDetails, setUserDetails] = useState(null);
  const [activeItem, setActiveItem] = useState(() => {
    const role = localStorage.getItem('userRole');
    if (role === 'admin') {
      return 'homeadmin'; // Set default active item for admin
    }
    return 'dashboard'; // Default for other roles
  });
  const [profile, setProfile] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isFrozen, setIsFrozen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsPosition, setSettingsPosition] = useState({ top: 0, left: 0 });
  const settingsButtonRef = useRef(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationPosition, setNotificationPosition] = useState({ top: 0, left: 0 });
  const bellButtonRef = useRef(null);
  const [pointerPosition, setPointerPosition] = useState(0);
  const [pointerVisible, setPointerVisible] = useState(true); // Force pointer to be visible initially (not controlled by state)
  const menuItemRefs = useRef({});
  const pointerRef = useRef(null); // Add ref for the pointer element
  const navigate = useNavigate();
  
  // State for User Management dropdown
  const [isUserManagementOpen, setIsUserManagementOpen] = useState(false);
  const userManagementRef = useRef(null);
 
  // Add new state for mobile sidebar visibility
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const sidebarRef = useRef(null);

  // Helper: should sidebar be hidden for admin?
  const shouldHideSidebar = userRole === 'admin' && isMobile;
  
  // Add the missing isMounted ref
  const isMounted = useRef(true);
  
  // Logout handler to navigate to homepage
  const handleLogoutCommand = () => {
    // Additional cleanup for Sidebar state can be done here if necessary,
    // e.g., resetting activeItem, isOpen, etc.
    // For now, the main goal is navigation.
    navigate('/');
  };
  
  // Add cleanup effect for isMounted
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  // Function to check if screen is mobile size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768); // Consider screens <= 768px as mobile
    };
    
    // Initial check
    checkScreenSize();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkScreenSize);
    
    // Cleanup function
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  // Handle clicks outside sidebar to close it on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && mobileOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setMobileOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, mobileOpen]);

  // Handle clicks outside User Management dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserManagementOpen && userManagementRef.current && !userManagementRef.current.contains(event.target)) {
        setIsUserManagementOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserManagementOpen]);
  
  // Toggle sidebar on pointer click for mobile
  const handlePointerClick = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    }
  };
  
  // Notify parent when sidebar state changes
  useEffect(() => {
    if (isMobile) {
      onExpandChange?.(mobileOpen);
    } else {
      onExpandChange?.(isOpen || isFrozen);
    }
  }, [isOpen, isFrozen, mobileOpen, isMobile, onExpandChange]);

  useEffect(() => {
    const userEmail = localStorage.getItem('userEmail');
    const userRole = localStorage.getItem('userRole');

    if (userEmail && userRole) {
      fetch(`${API_URL}/user/get_user?email=${userEmail}`)
        .then(res => res.json())
        .then(data => {
          // Store raw profile_picture value
          setProfile({
            name: `${data.firstName} ${data.lastName}`,
            id: data.id || data.idNumber,
            profile_picture: data.profile_picture, // MODIFIED: Store raw value
            role: userRole === 'faculty' ? 'Teacher' : 'Student'
          });
        })
        .catch(err => console.error('Error fetching profile:', err));
    }
  }, []);

  useEffect(() => {
    // Assume userRole is stored in localStorage (e.g., "student" or "faculty")
    const role = localStorage.getItem('userRole') || 'student';
    setUserRole(role);
  }, []);

  // NEW: retrieve profile picture using the API-based placeholder logic
  useEffect(() => {
    const storedPic = localStorage.getItem('profile_picture');
    setProfilePicture(storedPic); // MODIFIED: Store raw value
  }, []);

  const fetchUserDetails = async () => {
    const email = localStorage.getItem('userEmail');
    const userRole = localStorage.getItem('userRole');
    const studentID = localStorage.getItem('studentID');
    
    if (!email) return;

    try {
      const response = await fetch(`${API_URL}/user/get_user?email=${email}`);
      let userData = await response.json(); // userData.profile_picture should be raw
      
      if (userRole === 'student' && studentID) {
        try {
          const studentResponse = await fetch(`${API_URL}/user/get_student_details?studentID=${studentID}`);
          if (studentResponse.ok) {
            const studentData = await studentResponse.json();
            userData = { 
              ...userData, 
              program: studentData.program,
              year_section: studentData.year_section 
            };
          }
        } catch (error) {
          console.error('Error fetching student details:', error);
        }
      } else if (userRole === 'faculty' && userData.department) {
        if (userData.department.includes('/departments/')) {
          try {
            const deptID = userData.department.split('/').pop();
            const deptResponse = await fetch(`${API_URL}/account/departments`);
            if (deptResponse.ok) {
              const departments = await deptResponse.json();
              const deptMatch = departments.find(d => d.departmentID === deptID);
              if (deptMatch) {
                userData.department = deptMatch.departmentName;
              }
            }
          } catch (error) {
            console.error('Error fetching department:', error);
          }
        }
      }
      
      // MODIFIED: Store raw profile_picture from userData
      // The userData object from the response should already contain the raw profile_picture string (filename or null)
      setUserDetails(userData); 
      
      // MODIFIED: Update profilePicture state with the raw value from userData
      setProfilePicture(userData.profile_picture);

    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, []);

  // Update path mappings to include exact paths
  const menuPaths = {
    student: {
      homestudent: '/dashboard',
      appointments: '/appointments',
      history: '/history',
      grades: '/gradeview'
    },
    faculty: {
      dashboard: '/dashboard',
      classRecord: '/addgrade',
      appointments: '/appointments',
      schedule: '/teacher-schedule',
      history: '/history', // FIX: was 'past', should be 'history' to match menu item
      comparative: '/polycon-analysis'
    },
    admin: {
      homeadmin: '/homeadmin',
      semester: '/semester-management',
      add_users: '/admin',
      course: '/courses',
      program: '/programs',
      department: '/department',
      venues: '/venues',
      periods: '/periods',
      leaderboard: '/admin-consultation'
    }
  };

  // Add this new useEffect for initial admin navigation
  useEffect(() => {
    if (userRole === 'admin' && location.pathname === '/') {
      navigate('/homeadmin');
    }
  }, [userRole, location.pathname, navigate]);

  // Update the path matching useEffect to preserve active state on reload
  useEffect(() => {
    const currentPath = location.pathname;
    const userMenuPaths = menuPaths[userRole] || {};
    
    // Special handling for admin paths
    if (userRole === 'admin') {
      if (currentPath === '/') {
        setActiveItem('homeadmin');
      } else {
        // Find matching menu item by comparing paths
        const activeMenuItem = Object.entries(userMenuPaths).find(
          ([_, path]) => path === currentPath
        );
        if (activeMenuItem) {
          setActiveItem(activeMenuItem[0]);
        }
      }
    } else {
      // Handle non-admin paths as before
      const activeMenuItem = Object.entries(userMenuPaths).find(
        ([_, path]) => path === currentPath
      );
      if (activeMenuItem) {
        setActiveItem(activeMenuItem[0]);
      }
    }
  }, [location.pathname, userRole]);

  // Helper to render a menu item with an icon and a label that fades in
  const renderMenuItem = (id, IconComponent, label) => (
    <li 
      ref={el => menuItemRefs.current[id] = el}
      className={`sidebar-item relative h-9 flex items-center cursor-pointer px-2 ${
        activeItem === id ? 'bg-white outline outline-3 outline-[#54BEFF] sidebar-item-active' : ''
      }`}
      onClick={() => {
        setActiveItem(id);
        if (isMobile) setMobileOpen(false); // Close sidebar on item click in mobile
        const path = menuPaths[userRole]?.[id];
        if (path) {
          navigate(path);
        }
      }}
    >
      <div className="flex-shrink-0 -translate-y-[0px]">
        <IconComponent className={`w-6 h-6 icon-white ${
          activeItem === id ? 'active-icon' : ''
        }`} />
      </div>
      <span className={`sidebar-label ${
        ((isOpen && !isMobile) || mobileOpen) ? "opacity-100" : "opacity-0"
      } ${activeItem === id ? 'active-label' : ''}`}>
        {label}
      </span>
    </li>
  );

  // Helper to render a dropdown menu item
  const renderDropdownMenuItem = (id, IconComponent, label) => (
    <li 
      className={`dropdown-item relative flex items-center cursor-pointer px-4 py-3 rounded-lg transition-all duration-200 hover:bg-blue-50 hover:shadow-sm ${
        activeItem === id ? 'bg-blue-100 text-blue-700 shadow-sm' : 'text-gray-700 hover:text-blue-600'
      }`}
      onClick={() => {
        setActiveItem(id);
        setIsUserManagementOpen(false); // Close dropdown after selection
        if (isMobile) setMobileOpen(false); // Close sidebar on item click in mobile
        const path = menuPaths[userRole]?.[id];
        if (path) {
          navigate(path);
        }
      }}
    >
      <div className="flex-shrink-0 mr-3">
        <IconComponent className={`w-5 h-5 transition-colors duration-200 ${
          activeItem === id ? 'text-blue-600' : 'text-gray-500'
        }`} />
      </div>
      <span className="text-sm font-medium transition-colors duration-200">{label}</span>
      {activeItem === id && (
        <div className="absolute right-3 w-2 h-2 bg-blue-500 rounded-full"></div>
      )}
    </li>
  );

  // NEW: Update pointer position calculation for desktop screens
  useEffect(() => {
    if (isMobile) return;

    // Define User Management dropdown items
    const userManagementItems = ['add_users', 'course', 'program', 'department'];
    const isDropdownItem = userManagementItems.includes(activeItem);

    // Don't wait, calculate immediately
    const updatePointerPosition = () => {
      const activeMenuItem = menuItemRefs.current[activeItem];
      if (activeMenuItem && sidebarRef.current) {
        // Hide pointer if active item is a dropdown item
        if (isDropdownItem) {
          setPointerVisible(false);
          return;
        }

        // Simple calculation based on offset from top
        const position = activeMenuItem.offsetTop + (activeMenuItem.offsetHeight / 2);
        setPointerPosition(position);
        setPointerVisible(true);
        
        console.log("Desktop pointer updated:", {
          activeItem,
          position,
          element: activeMenuItem,
          isDropdownItem
        });
      }
    };
    
    // Run immediately after render
    updatePointerPosition();
    
    // Also add a small delay to catch any layout shifts
    const timer = setTimeout(updatePointerPosition, 100);
    return () => clearTimeout(timer);
  }, [activeItem, isMobile]);

  // Add resize observer for more accurate pointer positioning
  useEffect(() => {
    if (isMobile) return;
    
    const resizeObserver = new ResizeObserver(() => {
      const userManagementItems = ['add_users', 'course', 'program', 'department'];
      const isDropdownItem = userManagementItems.includes(activeItem);
      
      if (isDropdownItem) {
        setPointerVisible(false);
        return;
      }
      
      const ref = menuItemRefs.current[activeItem];
      if (ref) {
        const pos = ref.offsetTop + (ref.offsetHeight / 2);
        setPointerPosition(pos);
        setPointerVisible(true);
      }
    });
    
    // Observe the entire sidebar to detect any size changes
    if (sidebarRef.current) {
      resizeObserver.observe(sidebarRef.current);
    }
    
    return () => resizeObserver.disconnect();
  }, [activeItem, isMobile]);

  const handleModalClose = () => {
    // Keep sidebar expanded for a moment after modal closes
    setTimeout(() => {
      setIsFrozen(false);
    }, 200); // 200ms delay
    setShowUploadModal(false);
  };

  const handleSettingsClick = () => {
    // Position the popup at the middle-left of the screen
    setSettingsPosition({
      top: window.innerHeight / 2,
      left: 20
    });
    setShowSettings(!showSettings);
  };

  const handleBellClick = () => {
    if (bellButtonRef.current) {
      const rect = bellButtonRef.current.getBoundingClientRect();
      setNotificationPosition({
        // Position higher up to prevent overflow
        top: rect.top - 300, // Increased offset to move tray higher
        left: rect.right + 10
      });
    }
    setShowNotifications(!showNotifications);
  };

  // Derive display name and picture for avatar (initial or fetched)
  const initialName = profile?.name || '';
  const initialPic = profile?.profile_picture || profilePicture;  // from localStorage fallback
  const displayName = userDetails?.fullName || initialName;
  const displayPic = userDetails?.profile_picture || initialPic;

  return (
    <>
      {/* Hide sidebar for admin on mobile/tablet */}
      {shouldHideSidebar ? null : (
        <>
          {/* MOBILE: Standalone pointer when sidebar is closed */}
          {isMobile && !mobileOpen && (
            <div 
              className="fixed top-0 left-0 h-screen z-40 flex items-center cursor-pointer"
              style={{ width: '30px' }}
              onClick={handlePointerClick}
            >
              <div 
                className="mobile-pointer-icon" 
                style={{ 
                  position: 'absolute', 
                  right: '-7px',
                  pointerEvents: 'all' 
                }}
              >
                <PointerIcon width="48" height="48" className="fill-current text-[#057DCD]" />
              </div>
            </div>
          )}
          {/* Main sidebar */}
          <div 
            ref={sidebarRef}
            className={`sidebar fixed top-0 left-0 bg-[#057DCD] h-screen p-5 pt-8 z-40 transition-all duration-300 ${
              isMobile 
                ? mobileOpen ? 'translate-x-0 w-64' : '-translate-x-full' 
                : (isFrozen || isOpen) ? 'w-64' : 'w-20'
            }`}
            style={{ 
              borderTopRightRadius: '0px',
              borderBottomRightRadius: '0px'
            }}
            onMouseEnter={() => !isFrozen && !isMobile && setIsOpen(true)}
            onMouseLeave={() => !isFrozen && !isMobile && setIsOpen(false)}
          >
             {!isMobile && pointerVisible && (
           <div 
             className="pointer-icon desktop-pointer"
             style={{
               position: 'absolute',
               right: '-20px',
               top: `${pointerPosition}px`,
               transform: 'translateY(330%)',
               transition: 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // Added smooth transition
               zIndex: 999,
               pointerEvents: 'none',
             }}
           >
             <PointerIcon 
               width="40" 
               height="40" 
               className="fill-current text-[#057DCD]" 
             />
           </div>
         )}

        {/* For mobile: Add close button in the sidebar */}
        {isMobile && mobileOpen && (
          <button 
            className="absolute top-4 right-4 text-white z-50"
            onClick={() => setMobileOpen(false)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
            <div className="flex items-center mb-10">
              <div className="w-20 min-w-[5rem] -ml-5">
                <img src={logo} alt="Logo" className="w-full" />
              </div>
              <div className={`transition-opacity duration-300 ${((isOpen && !isMobile) || mobileOpen) ? 'opacity-100' : 'opacity-0'}`}>
                <h1 className="text-white font-bold text-xl">Polycon</h1>
              </div>
            </div>
            {userRole === 'student' && (
              <ul className="mt-2 space-y-3 relative">
                {renderMenuItem("homestudent", HomeIcon, "Home")}
                {renderMenuItem("appointments", UpcomingIcon, "Appointments")}
                {renderMenuItem("history", PastIcon, "History")}
                {renderMenuItem("grades", GradesIcon, "Grades")}
              </ul>
            )}
            {userRole === 'faculty' && (
              <ul className="mt-2 space-y-3 relative"> {/* Changed from mt-6 to mt-2 */}
                {renderMenuItem("dashboard", HomeIcon, "Home")}
                {renderMenuItem("appointments", UpcomingIcon, "Appointments")}
                {renderMenuItem("schedule", ScheduleIcon, "Schedule")}
                {renderMenuItem("history", PastIcon, "History")}
                {renderMenuItem("classRecord", ClassRecorderIcon, "Class Record")}
                {renderMenuItem("comparative", ComparativeIcon, "Polycon Analysis")}
              </ul>
            )}
            {userRole === 'admin' && !isMobile && (
              <ul className="mt-2 space-y-3 relative"> {/* Changed from mt-6 to mt-2 */}
                {renderMenuItem("homeadmin", HomeIcon, "Home")}
                
                {/* User Management Dropdown */}
                <li className="relative" ref={userManagementRef}>
                  <div 
                    className="sidebar-item relative h-9 flex items-center cursor-pointer px-2"
                    onClick={() => setIsUserManagementOpen(!isUserManagementOpen)}
                  >
                    <div className="flex-shrink-0 -translate-y-[0px]">
                      <svg 
                        className={`w-6 h-6 icon-white transition-transform duration-200 ${
                          isUserManagementOpen ? 'rotate-180' : ''
                        }`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    <span className={`sidebar-label ${
                      ((isOpen && !isMobile) || mobileOpen) ? "opacity-100" : "opacity-0"
                    }`}>
                      User Management
                    </span>
                  </div>
                  
                   {/* Dropdown Menu */}
                   <div className={`absolute left-full top-0 ml-3 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-3 z-[1000] dropdown-container ${isUserManagementOpen ? 'dropdown-open' : 'dropdown-closed'}`}>
                     <div className="dropdown-content">
                       <ul className="space-y-0.5">
                         {renderDropdownMenuItem("add_users", UserAdd, "Users")}
                         {renderDropdownMenuItem("course", CourseAdd, "Courses")}
                         {renderDropdownMenuItem("program", ProgramAdd, "Programs")}
                         {renderDropdownMenuItem("department", DepartmentAdd, "Departments")}
                       </ul>
                     </div>
                   </div>
                </li>
                
                {/* Remaining items */}
                {renderMenuItem("venues", VenuesIcon, "Venues")}
                {renderMenuItem("periods", PeriodsIcon, "Periods")}
                {renderMenuItem("semester", SemesterAdd, "Semesters")}
                {renderMenuItem("leaderboard", Leaderboard, "Leaderboard")}
              </ul>
            )}
            {/* ...existing bell/settings/profile... */}
            <div className="absolute bottom-32 left-0 pl-7 z-50">
              <ul className="flex flex-col items-center space-y-4">
                <li 
                  ref={bellButtonRef}
                  onClick={handleBellClick}
                  className={`no-hover-item relative h-8 flex items-center cursor-pointer ${((isOpen && !isMobile) || mobileOpen) ? 'opacity-100' : 'opacity-0'}`}
                >
                  <BellIcon className="w-6 h-6 bell-icon -mr-2" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 left-3 block w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-white"></span>
                  )}
                </li>
                <li 
                  ref={settingsButtonRef}
                  onClick={handleSettingsClick}
                  className={`no-hover-item relative h-8 flex items-center cursor-pointer ${((isOpen && !isMobile) || mobileOpen) ? 'opacity-100' : 'opacity-0'}`}
                >
                  <SettingsIcon className="w-6 h-6 justify -mr-2" />
                </li>
              </ul>
            </div>
            <div className="absolute bottom-10 left-0 pl-3 w-full pr-2 z-50">
              <div className="flex items-center space-x-2">
                <div className="flex-shrink-0">
                  <div 
                    className="rounded-full p-1 bg-[#54BEFF] relative group cursor-pointer"
                    onClick={() => {
                      setShowUploadModal(true);
                      setIsFrozen(true);
                    }}
                  >
                    <div className="rounded-full p-1 bg-white">
                      <div className="relative">
                        <img
                          src={getProfilePictureUrl(displayPic, displayName)}
                          alt={displayName || 'Profile'}
                          className="rounded-full w-10 h-10"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-200">
                          <span className="text-white text-xs">Edit</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`flex-grow min-w-0 ${((isOpen && !isMobile) || mobileOpen) ? "opacity-100" : "opacity-0"}`}>
                  {userDetails && (
                    <>
                      <p className="text-white text-sm font-semibold truncate">
                        {userDetails.firstName} {userDetails.lastName}
                      </p>
                      <p className="text-gray-200 text-xs truncate">
                        {userDetails.idNumber || userDetails.id}
                      </p>
                      <p className="text-gray-200 text-xs truncate">
                        {userRole === 'student' ? 
                          `${userDetails.program || ''} ${userDetails.year_section || ''}` :
                          userRole === 'faculty' ? 
                            userDetails.department || 'Loading...' :
                            'Admin'}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

            {/* Settings popups */}
      <SettingsPopup 
        isVisible={showSettings} 
        onClose={() => setShowSettings(false)}
        position={settingsPosition}
        onLogout={handleLogoutCommand} // Pass the logout handler
        userEmail={userDetails?.email} // Pass userEmail if needed by SettingsPopup
      />

      {/* Notification Tray */}
      <NotificationTray 
        isVisible={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <ProfilePictureUploader
            onClose={handleModalClose}
            onSuccess={(newProfileUrl) => {
              fetchUserDetails();
              setProfilePicture(newProfileUrl);
              handleModalClose();
            }}
          />
        </div>
      )}
    </>
  );
};

export default Sidebar;

